import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";

/**
 * Tax optimisation API (feature key: tax_optimization).
 *
 * Two things this module deliberately does NOT do:
 *
 *  - It never computes or formats a tax amount client-side. Every figure comes
 *    from the server together with its explanation, its rule-set version and
 *    its calculation date. A number recomputed in the browser would drift from
 *    the one the customer is told to file.
 *  - It never treats a missing value as zero. `readiness` is a first-class
 *    query because the honest answer to "what do I owe?" is often "we cannot
 *    tell you yet, and here is exactly what is missing".
 */

export const TaxKeys = {
  summary: (fleetId: number, year: number) => ["tax-summary", fleetId, year] as const,
  lines: (fleetId: number, assessmentId: number, filters: unknown) =>
    ["tax-lines", fleetId, assessmentId, filters] as const,
  readiness: (fleetId: number, year: number) => ["tax-readiness", fleetId, year] as const,
  opportunities: (fleetId: number, status: string) => ["tax-opportunities", fleetId, status] as const,
  profile: (fleetId: number, vehicleId: number) => ["tax-profile", fleetId, vehicleId] as const,
  documents: (fleetId: number, filters: unknown) => ["fiscal-documents", fleetId, filters] as const,
  document: (fleetId: number, documentId: number) => ["fiscal-document", fleetId, documentId] as const,
  discoverable: (fleetId: number) => ["fiscal-discoverable", fleetId] as const,
};

export type LineStatus = "OK" | "INCOMPLETE" | "EXEMPT";

export interface TaxExplanation {
  inputs: Record<string, unknown>;
  formula: string;
  legal_ref: string | null;
  warnings: string[];
  scale?: Record<string, unknown>;
  rule_set?: { id: number; fiscal_year: number; status?: string };
  note?: string;
}

export interface TaxAssessmentLine {
  id: number;
  vehicle_id: number | null;
  component: string;
  period_start: string;
  period_end: string;
  base_value: number | null;
  rate_or_bracket: string | null;
  proration: number;
  amount_cents: number;
  line_status: LineStatus;
  explanation: TaxExplanation;
}

export interface TaxSummary {
  assessment_id?: number;
  fiscal_year: number;
  revision?: number;
  status: string;
  currency?: string;
  total_cents?: number;
  totals_by_component?: Record<string, number>;
  non_payable_note?: string;
  coverage?: { vehicles_total: number; vehicles_complete: number; ratio: number };
  rule_set_ids?: number[];
  warnings?: Array<{ code: string; message: string }>;
  computed_at?: string | null;
  reviewed_at?: string | null;
  filing_ready?: boolean;
  readiness?: TaxReadiness;
  disclaimer: string;
}

export interface TaxReadiness {
  fiscal_year: number;
  vehicles_total: number;
  vehicles_with_profile: number;
  vehicles_without_profile: number;
  vehicles_ready: number;
  readiness_ratio: number;
  can_compute: boolean;
  is_filing_ready: boolean;
  gaps: Array<{ field: string; label: string; vehicles_blocked: number }>;
  unverified_count: number;
  blocking_vehicles: Array<{ vehicle_id: number; plate: string | null; missing_fields: string[] }>;
  next_best_action: { code: string; message: string; hint: string; field?: string } | null;
}

export interface TaxOpportunity {
  id: number;
  vehicle_id: number | null;
  code: string;
  severity: "INFO" | "LOW" | "MEDIUM" | "HIGH";
  title: string;
  message: string;
  action: string | null;
  estimated_impact_cents: number | null;
  evidence: Record<string, unknown> | null;
  status: string;
}

/**
 * A coffre-fort document that could feed the fiscal module but has not been
 * pulled in yet.
 */
export interface DiscoverableDocument {
  strongbox_id: number;
  name: string | null;
  strongbox_type: string;
  doc_type: string | null;
  vehicle_id: number;
  vehicle_plate: string | null;
  created_at: string;
  already_imported: boolean;
  fiscal_status: string | null;
}

export interface FiscalDocument {
  id: number;
  vehicle_id: number | null;
  source_strongbox_id?: number | null;
  doc_type: string;
  original_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  status: "UPLOADED" | "EXTRACTING" | "NEEDS_REVIEW" | "APPLIED" | "REJECTED" | "FAILED";
  extracted: { values: Record<string, unknown>; requires_human_review: boolean } | null;
  applied_changes: Record<string, unknown> | null;
  error: string | null;
  created_at: string;
}

/** Annual summary. Returns status NOT_COMPUTED with a readiness payload on first run. */
export const useTaxSummary = (fleetId: number, fiscalYear: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.summary(fleetId, fiscalYear),
    enabled: enabled && !!fleetId,
    queryFn: async (): Promise<TaxSummary> => {
      const response = await axios.get(`/companies/${fleetId}/tax/assessments`, {
        params: { fiscal_year: fiscalYear },
      });
      return response?.data;
    },
  });

  return { summary: data, ...res };
};

export const useTaxLines = (
  fleetId: number,
  assessmentId: number | undefined,
  filters: { vehicle_id?: number; component?: string; status?: LineStatus; page?: number } = {}
) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.lines(fleetId, assessmentId ?? 0, filters),
    enabled: !!fleetId && !!assessmentId,
    queryFn: async () => {
      const response = await axios.get(
        `/companies/${fleetId}/tax/assessments/${assessmentId}/lines`,
        { params: filters }
      );
      return response?.data;
    },
  });

  return { linesPayload: data, ...res };
};

export const useTaxReadiness = (fleetId: number, fiscalYear: number) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.readiness(fleetId, fiscalYear),
    enabled: !!fleetId,
    queryFn: async (): Promise<TaxReadiness> => {
      const response = await axios.get(`/companies/${fleetId}/tax/readiness`, {
        params: { fiscal_year: fiscalYear },
      });
      return response?.data;
    },
  });

  return { readiness: data, ...res };
};

/**
 * Queues a recomputation. The endpoint answers 202 — the result appears on the
 * next summary fetch, so callers should invalidate and poll rather than expect
 * a figure back from this call.
 */
export const useComputeAssessment = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { fiscal_year: number; force?: boolean }) => {
      const response = await axios.post(`/companies/${fleetId}/tax/assessments`, payload);
      return response?.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TaxKeys.summary(fleetId, variables.fiscal_year) });
      queryClient.invalidateQueries({ queryKey: TaxKeys.readiness(fleetId, variables.fiscal_year) });
    },
  });
};

export const useTaxOpportunities = (fleetId: number, status = "open") => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.opportunities(fleetId, status),
    enabled: !!fleetId,
    queryFn: async (): Promise<{ opportunities: TaxOpportunity[]; total_impact_cents: number }> => {
      const response = await axios.get(`/companies/${fleetId}/tax/opportunities`, { params: { status } });
      return response?.data;
    },
  });

  return { opportunities: data?.opportunities ?? [], totalImpactCents: data?.total_impact_cents ?? 0, ...res };
};

export const useHandleOpportunity = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: number; status: "ACKNOWLEDGED" | "DISMISSED" | "RESOLVED" }) => {
      const response = await axios.post(
        `/companies/${fleetId}/tax/opportunities/${payload.id}`,
        { status: payload.status }
      );
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-opportunities", fleetId] });
    },
  });
};

export const useVehicleFiscalProfile = (fleetId: number, vehicleId: number | undefined) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.profile(fleetId, vehicleId ?? 0),
    enabled: !!fleetId && !!vehicleId,
    queryFn: async () => {
      const response = await axios.get(`/companies/${fleetId}/tax/vehicles/${vehicleId}/profile`);
      return response?.data;
    },
  });

  return { profilePayload: data, ...res };
};

export const useSaveVehicleFiscalProfile = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { vehicleId: number; values: Record<string, unknown> }) => {
      const response = await axios.put(
        `/companies/${fleetId}/tax/vehicles/${payload.vehicleId}/profile`,
        payload.values
      );
      return response?.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TaxKeys.profile(fleetId, variables.vehicleId) });
      queryClient.invalidateQueries({ queryKey: ["tax-readiness", fleetId] });
    },
  });
};

/** Arbitrage simulation. Synchronous and bounded server-side (max 6 scenarios). */
export const useSimulate = (fleetId: number) =>
  useMutation({
    mutationFn: async (payload: {
      scenarios: Array<{ label: string; vehicle: Record<string, unknown> }>;
      start_year?: number;
      years?: number;
    }) => {
      const response = await axios.post(`/companies/${fleetId}/tax/simulations`, payload);
      return response?.data;
    },
  });

// ── Documents ────────────────────────────────────────────────────────────────

export const useFiscalDocuments = (
  fleetId: number,
  filters: { status?: string; doc_type?: string; vehicle_id?: number } = {}
) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.documents(fleetId, filters),
    enabled: !!fleetId,
    // Extraction is asynchronous; poll while anything is still in flight so the
    // review queue fills in without the user reloading the page.
    refetchInterval: (query) => {
      const payload = query.state.data as { documents?: { data?: FiscalDocument[] } } | undefined;
      const rows = payload?.documents?.data ?? [];
      const pending = rows.some((d) => d.status === "UPLOADED" || d.status === "EXTRACTING");
      return pending ? 4000 : false;
    },
    queryFn: async () => {
      const response = await axios.get(`/companies/${fleetId}/fiscal-documents`, { params: filters });
      return response?.data;
    },
  });

  return { documentsPayload: data, ...res };
};

/**
 * Multipart upload. Content-Type is explicitly unset so the browser can add the
 * multipart boundary — the axios instance defaults to application/json and a
 * forgotten override here produces a 422 that looks like a validation bug.
 */
export const useUploadFiscalDocument = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { file: File; doc_type: string; vehicle_id?: number }) => {
      const form = new FormData();
      form.append("file", payload.file);
      form.append("doc_type", payload.doc_type);
      if (payload.vehicle_id) form.append("vehicle_id", String(payload.vehicle_id));

      const response = await axios.post(`/companies/${fleetId}/fiscal-documents`, form, {
        headers: { "Content-Type": undefined as unknown as string },
      });
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] });
    },
  });
};

export const useFiscalDocument = (fleetId: number, documentId: number | undefined) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.document(fleetId, documentId ?? 0),
    enabled: !!fleetId && !!documentId,
    queryFn: async () => {
      const response = await axios.get(`/companies/${fleetId}/fiscal-documents/${documentId}`);
      return response?.data;
    },
  });

  return { documentPayload: data, ...res };
};

/** Confirm reviewed values. Nothing is written before this call. */
export const useApplyFiscalDocument = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      documentId: number;
      vehicle_id?: number;
      values: Record<string, unknown>;
    }) => {
      const response = await axios.post(
        `/companies/${fleetId}/fiscal-documents/${payload.documentId}/apply`,
        { vehicle_id: payload.vehicle_id, values: payload.values }
      );
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] });
      queryClient.invalidateQueries({ queryKey: ["tax-readiness", fleetId] });
      queryClient.invalidateQueries({ queryKey: ["tax-profile", fleetId] });
    },
  });
};

/**
 * Documents already in the coffre-fort that the fiscal module could use.
 *
 * This is the answer to the oldest complaint about the feature: an established
 * customer with every carte grise on file opened Optimisation fiscale, saw a
 * completeness of zero, and was asked to upload them all again. Nothing is
 * written by this query — it only reports what could be reused.
 */
export const useDiscoverableDocuments = (fleetId: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: TaxKeys.discoverable(fleetId),
    enabled: enabled && !!fleetId,
    queryFn: async (): Promise<{
      candidates: DiscoverableDocument[];
      total: number;
      max_per_run: number;
      note: string;
    }> => {
      const response = await axios.get(`/companies/${fleetId}/fiscal-documents/discoverable`);
      return response?.data;
    },
  });

  return {
    candidates: data?.candidates ?? [],
    discoverableTotal: data?.total ?? 0,
    maxPerRun: data?.max_per_run ?? 200,
    ...res,
  };
};

/**
 * Pull selected coffre-fort documents into the extraction pipeline.
 *
 * Omitting `strongbox_ids` imports everything eligible, capped server-side.
 * The imported documents land in NEEDS_REVIEW like any other upload — this
 * never writes to a fiscal profile on its own.
 */
export const useImportExistingDocuments = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { strongbox_ids?: number[] } = {}) => {
      const response = await axios.post(
        `/companies/${fleetId}/fiscal-documents/import-existing`,
        payload
      );
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] });
      queryClient.invalidateQueries({ queryKey: TaxKeys.discoverable(fleetId) });
      // Readiness is what the user is actually trying to move; refreshing it
      // here is what makes the import feel like it did something.
      queryClient.invalidateQueries({ queryKey: ["tax-readiness", fleetId] });
    },
  });
};

export const useRejectFiscalDocument = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: number) => {
      const response = await axios.post(`/companies/${fleetId}/fiscal-documents/${documentId}/reject`);
      return response?.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] }),
  });
};

/**
 * Corriger un import : re-classer et/ou re-rattacher.
 *
 * The two mistakes users actually make. Until these existed, a purchase
 * invoice uploaded as a carte grise extracted against the wrong field
 * whitelist and could never be applied, and the only escape was to reject it
 * and upload it again — which the server then refused as a duplicate, because
 * the content hash is the same file.
 *
 * `re_extracting` in the response is not decoration: changing the type resets
 * the document to UPLOADED server-side and requeues extraction, because the
 * type selects the field whitelist, the apply target AND the extractor prompt.
 * Surface it, or a document the user had queued for review silently goes back
 * to the start.
 *
 * `vehicle_id: null` is a meaningful value (detach), so the payload has to
 * distinguish an absent key from a null one. Sending neither field is a 422.
 */
export const useUpdateFiscalDocument = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      documentId: number;
      doc_type?: string;
      vehicle_id?: number | null;
    }) => {
      const { documentId, ...body } = payload;
      const response = await axios.patch(
        `/companies/${fleetId}/fiscal-documents/${documentId}`,
        body
      );
      return response?.data as {
        document: FiscalDocument;
        re_extracting: boolean;
        message: string;
      };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] });
      queryClient.invalidateQueries({ queryKey: TaxKeys.document(fleetId, variables.documentId) });
    },
  });
};

/**
 * Replace the bytes behind a document, keeping its identity.
 *
 * Not equivalent to delete + re-upload: the row, its place in the review queue
 * and its provenance survive, and a failure here leaves the original intact
 * rather than leaving the user with nothing.
 *
 * Same multipart caveat as the upload hook — the JSON Content-Type default has
 * to be unset so the browser can write the boundary itself.
 */
export const useReplaceFiscalDocumentFile = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { documentId: number; file: File }) => {
      const form = new FormData();
      form.append("file", payload.file);

      const response = await axios.post(
        `/companies/${fleetId}/fiscal-documents/${payload.documentId}/replace`,
        form,
        { headers: { "Content-Type": undefined as unknown as string } }
      );
      return response?.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] });
      queryClient.invalidateQueries({ queryKey: TaxKeys.document(fleetId, variables.documentId) });
    },
  });
};

/**
 * Delete a document.
 *
 * The server answers 409 for an APPLIED document — it is the piece
 * justificative behind a figure already written to a fiscal profile or the
 * cost ledger, and removing it would leave a declared number with no support.
 * The UI must not offer the action in that state: the guard is the server's,
 * but a button that only explains itself after being pressed is worse than no
 * button.
 *
 * Readiness and the coffre-fort candidates are both invalidated. Deleting a
 * pending grey card changes what the completeness screen is waiting for, and a
 * document that came from the coffre-fort becomes offerable again.
 */
export const useDeleteFiscalDocument = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: number) => {
      const response = await axios.delete(`/companies/${fleetId}/fiscal-documents/${documentId}`);
      return response?.data as { deleted: boolean; purged: boolean; message: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", fleetId] });
      queryClient.invalidateQueries({ queryKey: ["tax-readiness", fleetId] });
      queryClient.invalidateQueries({ queryKey: TaxKeys.discoverable(fleetId) });
    },
  });
};

/** Cents → euros, formatted once, in one place. */
export const formatCents = (cents: number | null | undefined, locale = "fr-FR"): string => {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(cents / 100);
};

export const COMPONENT_LABELS: Record<string, string> = {
  co2_tax: "Taxe annuelle CO2",
  pollutant_tax: "Taxe polluants atmosphériques",
  non_deductible_depreciation: "Amortissements non déductibles",
  vat_non_recoverable: "TVA non récupérable",
  benefit_in_kind: "Avantages en nature",
};

/** Components that are not taxes payable and must never be summed with them. */
export const NON_PAYABLE_COMPONENTS = [
  "non_deductible_depreciation",
  "benefit_in_kind",
  "vat_non_recoverable",
];
